import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { places } from "@/mocks/places";
import { restaurants } from "@/mocks/restaurants";
import VisitPlanModal from "./components/VisitPlanModal";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageView } from "@/hooks/usePageView";

// City → SSS page mapping for places
const placeCityFaqLinks: Record<string, { path: string; label: string }> = {
  "İstanbul": { path: "/istanbul-sss", label: "İstanbul Sık Sorulan Sorular" },
  "Nevşehir": { path: "/kapadokya-sss", label: "Kapadokya Sık Sorulan Sorular" },
  "Antalya": { path: "/antalya-sss", label: "Antalya Sık Sorulan Sorular" },
  "İzmir": { path: "/izmir-sss", label: "İzmir Sık Sorulan Sorular" },
  "Mardin": { path: "/mardin-sss", label: "Mardin Sık Sorulan Sorular" },
  "Rize": { path: "/trabzon-sss", label: "Trabzon & Karadeniz Sık Sorulan Sorular" },
  "Trabzon": { path: "/trabzon-sss", label: "Trabzon Sık Sorulan Sorular" },
  "Sakarya": { path: "/sakarya-sss", label: "Sakarya Sık Sorulan Sorular" },
  "Kocaeli": { path: "/kocaeli-sss", label: "Kocaeli Sık Sorulan Sorular" },
  "Bolu": { path: "/bolu-sss", label: "Bolu Sık Sorulan Sorular" },
};

// ── SEO helpers ──────────────────────────────────────────────
const setMetaTag = (name: string, content: string, isProperty = false) => {
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLinkTag = (rel: string, href: string) => {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const injectSchema = (id: string, schema: object) => {
  const existing = document.getElementById(`schema-place-${id}`);
  if (existing) existing.remove();
  const script = document.createElement("script");
  script.id = `schema-place-${id}`;
  script.type = "application/ld+json";
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
};
// ─────────────────────────────────────────────────────────────

// ── Practical Info Data ──────────────────────────────────────
interface PracticalInfo {
  transport: { icon: string; label: string; detail: string }[];
  entry: { label: string; price: string; note?: string }[];
  accommodation: { name: string; type: string; priceRange: string }[];
  tips: string[];
  mapZoom: number;
}

const practicalData: Record<string, PracticalInfo> = {
  "kapadokya": {
    transport: [
      { icon: "ri-flight-takeoff-line", label: "Uçak", detail: "Nevşehir Kapadokya Havalimanı veya Kayseri Erkilet Havalimanı (1.5 saat transfer)" },
      { icon: "ri-bus-line", label: "Otobüs", detail: "İstanbul'dan yaklaşık 10 saat, Ankara'dan 4 saat" },
      { icon: "ri-car-line", label: "Araç", detail: "Ankara'dan 3 saat, İstanbul'dan 7 saat (E90 otoyolu)" },
    ],
    entry: [
      { label: "Göreme Açık Hava Müzesi", price: "₺500", note: "Karanlık Kilise ayrıca ücretlidir" },
      { label: "Yeraltı Şehirleri (Derinkuyu/Kaymaklı)", price: "₺400" },
      { label: "Balon Turu", price: "₺3.500 – ₺5.000", note: "Kişi başı, şirkete göre değişir" },
    ],
    accommodation: [
      { name: "Museum Hotel", type: "Butik Kaya Otel", priceRange: "₺₺₺₺" },
      { name: "Argos in Cappadocia", type: "Lüks Kaya Otel", priceRange: "₺₺₺₺" },
      { name: "Kelebek Cave Hotel", type: "Kaya Otel", priceRange: "₺₺₺" },
    ],
    tips: ["Balon turları için 3-4 gün önceden rezervasyon yapın", "Sabah 5:30'da kalkarak gün doğumunu kaçırmayın", "Kış aylarında (Aralık-Şubat) fiyatlar %40 düşer"],
    mapZoom: 12,
  },
  "pamukkale": {
    transport: [
      { icon: "ri-flight-takeoff-line", label: "Uçak", detail: "Denizli Çardak Havalimanı (65 km, 1 saat transfer)" },
      { icon: "ri-bus-line", label: "Otobüs", detail: "İzmir'den 3 saat, İstanbul'dan 7 saat" },
      { icon: "ri-car-line", label: "Araç", detail: "İzmir'den 2.5 saat, Antalya'dan 3 saat" },
    ],
    entry: [
      { label: "Pamukkale & Hierapolis", price: "₺600", note: "Travertenler ve antik kent dahil" },
      { label: "Kleopatra Havuzu", price: "₺350", note: "Ayrıca ücretlidir" },
    ],
    accommodation: [
      { name: "Doga Thermal Hotel", type: "Termal Otel", priceRange: "₺₺₺" },
      { name: "Melrose House Hotel", type: "Butik Otel", priceRange: "₺₺" },
      { name: "Hal-Tur Hotel", type: "Aile Oteli", priceRange: "₺₺" },
    ],
    tips: ["Travertenlerde yürürken ayakkabılarınızı çıkarmanız zorunludur", "Gün batımında ziyaret için 16:00'dan sonra gelin", "Sabah erken saatlerde kalabalık azdır"],
    mapZoom: 14,
  },
  "efes": {
    transport: [
      { icon: "ri-flight-takeoff-line", label: "Uçak", detail: "İzmir Adnan Menderes Havalimanı (80 km, 1 saat transfer)" },
      { icon: "ri-bus-line", label: "Otobüs", detail: "İzmir'den Selçuk'a 1 saat, Kuşadası'ndan 30 dakika" },
      { icon: "ri-train-line", label: "Tren", detail: "İzmir'den Selçuk'a tren bağlantısı mevcut (1.5 saat)" },
    ],
    entry: [
      { label: "Efes Antik Kenti", price: "₺700" },
      { label: "Yamaç Evleri (ek)", price: "₺350", note: "Ayrıca ücretlidir, kesinlikle görün" },
      { label: "Efes Müzesi (Selçuk)", price: "₺200" },
    ],
    accommodation: [
      { name: "Naz Han Hotel", type: "Butik Otel (Şirince)", priceRange: "₺₺₺" },
      { name: "Bella Hotel Selçuk", type: "Aile Oteli", priceRange: "₺₺" },
      { name: "Kuşadası Otelleri", type: "Sahil Otelleri", priceRange: "₺₺ – ₺₺₺" },
    ],
    tips: ["Yaz aylarında sabah 8'de açılışta girin, öğleden sonra çok sıcak olur", "Rehberli tur almak deneyimi çok zenginleştirir", "Şirince köyü ile birleştirin"],
    mapZoom: 14,
  },
  "nemrut-dagi": {
    transport: [
      { icon: "ri-flight-takeoff-line", label: "Uçak", detail: "Adıyaman Havalimanı (90 km transfer) veya Malatya Havalimanı" },
      { icon: "ri-bus-line", label: "Otobüs", detail: "Adıyaman'dan minibüs veya tur araçları" },
      { icon: "ri-car-line", label: "Araç", detail: "Adıyaman'dan 2 saat, son 8 km yürüyüş veya minibüs" },
    ],
    entry: [
      { label: "Nemrut Dağı Milli Parkı", price: "₺200" },
      { label: "Karakuş Tümülüsü", price: "Ücretsiz" },
      { label: "Cendere Köprüsü", price: "Ücretsiz" },
    ],
    accommodation: [
      { name: "Zeus Hotel Kahta", type: "Butik Otel", priceRange: "₺₺" },
      { name: "Kommagene Hotel", type: "Aile Oteli", priceRange: "₺₺" },
      { name: "Adıyaman Otelleri", type: "Şehir Otelleri", priceRange: "₺ – ₺₺" },
    ],
    tips: ["Gün doğumu için gece 03:00'da yola çıkın", "Zirve soğuk olabilir, katmanlı giyinin", "Tur şirketleriyle gitmek en pratik seçenek"],
    mapZoom: 12,
  },
  "gobeklitepe": {
    transport: [
      { icon: "ri-flight-takeoff-line", label: "Uçak", detail: "Şanlıurfa GAP Havalimanı (45 km transfer)" },
      { icon: "ri-bus-line", label: "Otobüs", detail: "Şanlıurfa şehir merkezinden servis araçları" },
      { icon: "ri-car-line", label: "Araç", detail: "Şanlıurfa'dan 15 km, yaklaşık 20 dakika" },
    ],
    entry: [
      { label: "Göbeklitepe Arkeoloji Parkı", price: "₺300" },
      { label: "Şanlıurfa Arkeoloji Müzesi", price: "₺150", note: "Mutlaka görün" },
    ],
    accommodation: [
      { name: "Hilton Garden Inn Şanlıurfa", type: "Zincir Otel", priceRange: "₺₺₺" },
      { name: "Manici Hotel", type: "Butik Otel", priceRange: "₺₺" },
      { name: "Urfa Otelleri", type: "Şehir Otelleri", priceRange: "₺ – ₺₺" },
    ],
    tips: ["Rehberli tur almak şiddetle tavsiye edilir", "Şanlıurfa Balıklıgöl ile birleştirin", "Yaz aylarında çok sıcak olur, sabah erken gidin"],
    mapZoom: 14,
  },
  "bodrum": {
    transport: [
      { icon: "ri-flight-takeoff-line", label: "Uçak", detail: "Bodrum Milas Havalimanı (36 km, 45 dakika transfer)" },
      { icon: "ri-bus-line", label: "Otobüs", detail: "İstanbul'dan 10 saat, İzmir'den 3 saat" },
      { icon: "ri-ship-line", label: "Feribot", detail: "Yunan adalarından (Kos, Rodos) feribot bağlantısı" },
    ],
    entry: [
      { label: "Bodrum Kalesi & Sualtı Müzesi", price: "₺400" },
      { label: "Halikarnas Mozolesi Kalıntıları", price: "₺150" },
    ],
    accommodation: [
      { name: "Mandarin Oriental Bodrum", type: "Lüks Resort", priceRange: "₺₺₺₺" },
      { name: "Kempinski Hotel Barbaros Bay", type: "Lüks Otel", priceRange: "₺₺₺₺" },
      { name: "Gümüşlü Butik Oteller", type: "Butik Otel", priceRange: "₺₺₺" },
    ],
    tips: ["Temmuz-Ağustos çok kalabalık, Haziran veya Eylül tercih edin", "Gümüşlü ve Türkbükü daha sakin alternatifler", "Mavi yolculuk için en az 3 gün ayırın"],
    mapZoom: 13,
  },
  "olüdeniz": {
    transport: [
      { icon: "ri-flight-takeoff-line", label: "Uçak", detail: "Dalaman Havalimanı (50 km, 1 saat transfer)" },
      { icon: "ri-bus-line", label: "Otobüs", detail: "Fethiye'den dolmuş (20 dakika)" },
      { icon: "ri-car-line", label: "Araç", detail: "Fethiye'den 15 km, 20 dakika" },
    ],
    entry: [
      { label: "Ölüdeniz Tabiat Parkı (plaj)", price: "₺150" },
      { label: "Yamaç Paraşütü (tandem)", price: "₺2.500 – ₺3.500", note: "Kişi başı, şirkete göre değişir" },
      { label: "Kelebekler Vadisi (tekne)", price: "₺200 – ₺350", note: "Tekne turu dahil" },
    ],
    accommodation: [
      { name: "Oyster Residences", type: "Butik Otel", priceRange: "₺₺₺" },
      { name: "Meri Hotel", type: "Sahil Oteli", priceRange: "₺₺₺" },
      { name: "Fethiye Otelleri", type: "Şehir Otelleri", priceRange: "₺₺" },
    ],
    tips: ["Yamaç paraşütü için sabah erken saatler daha sakin", "Kelebekler Vadisi'ne tekneyle gidin, yürüyüş zordur", "Likya Yolu için Fethiye'den başlayın"],
    mapZoom: 14,
  },
  "mardin": {
    transport: [
      { icon: "ri-flight-takeoff-line", label: "Uçak", detail: "Mardin Havalimanı (20 km, 30 dakika transfer)" },
      { icon: "ri-bus-line", label: "Otobüs", detail: "Ankara'dan 12 saat, İstanbul'dan 16 saat" },
      { icon: "ri-car-line", label: "Araç", detail: "Diyarbakır'dan 1.5 saat, Gaziantep'ten 3 saat" },
    ],
    entry: [
      { label: "Deyrulzafaran Manastırı", price: "₺100" },
      { label: "Kasımiye Medresesi", price: "₺50" },
      { label: "Mardin Müzesi", price: "₺100" },
    ],
    accommodation: [
      { name: "Artuklu Kervansaray", type: "Tarihi Konak Otel", priceRange: "₺₺₺" },
      { name: "Reyhani Kasrı", type: "Butik Konak", priceRange: "₺₺₺" },
      { name: "Bağlarbaşı Konağı", type: "Butik Otel", priceRange: "₺₺" },
    ],
    tips: ["Gün batımında terasta olmak için 17:00'da konumlanın", "Çarşıda gümüş takı almak için pazarlık yapın", "Deyrulzafaran için sabah erken gidin"],
    mapZoom: 14,
  },
  "safranbolu": {
    transport: [
      { icon: "ri-bus-line", label: "Otobüs", detail: "Ankara'dan 3 saat, İstanbul'dan 5 saat" },
      { icon: "ri-car-line", label: "Araç", detail: "Ankara'dan 2.5 saat (D-100 karayolu)" },
      { icon: "ri-train-line", label: "Tren", detail: "Karabük istasyonuna tren, oradan 10 km transfer" },
    ],
    entry: [
      { label: "Tarihi Konaklar (dış)", price: "Ücretsiz" },
      { label: "Cinci Hanı", price: "₺50" },
      { label: "Safranbolu Müzesi", price: "₺80" },
    ],
    accommodation: [
      { name: "Cinci Han Otel", type: "Tarihi Han Otel", priceRange: "₺₺₺" },
      { name: "Havuzlu Asmazlar Konağı", type: "Tarihi Konak", priceRange: "₺₺₺" },
      { name: "Gülevi Butik Otel", type: "Butik Otel", priceRange: "₺₺" },
    ],
    tips: ["Tarihi bir konakta konaklayın, deneyimi tamamlar", "Kış aylarında kar altındaki Safranbolu büyülüür", "Yerel lokumları ve safran çayını mutlaka alın"],
    mapZoom: 15,
  },
  "ayder-yaylasi": {
    transport: [
      { icon: "ri-flight-takeoff-line", label: "Uçak", detail: "Trabzon Havalimanı (120 km, 2 saat transfer)" },
      { icon: "ri-bus-line", label: "Otobüs", detail: "Rize'den minibüs (1.5 saat)" },
      { icon: "ri-car-line", label: "Araç", detail: "Rize'den 60 km, 1.5 saat (dağ yolu)" },
    ],
    entry: [
      { label: "Ayder Termal Tesisleri", price: "₺200 – ₺400", note: "Tesise göre değişir" },
      { label: "Milli Park Girişi", price: "₺50" },
    ],
    accommodation: [
      { name: "Ayder Doğa Otel", type: "Yayla Oteli", priceRange: "₺₺₺" },
      { name: "Fırtına Vadisi Bungalov", type: "Bungalov", priceRange: "₺₺" },
      { name: "Yerel Pansiyonlar", type: "Pansiyon", priceRange: "₺" },
    ],
    tips: ["Haziran-Eylül en ideal dönem", "Kaçkar trekking için rehber tutun", "Muhlama ve hamsi tava mutlaka deneyin"],
    mapZoom: 13,
  },
  "halfeti": {
    transport: [
      { icon: "ri-flight-takeoff-line", label: "Uçak", detail: "Şanlıurfa GAP Havalimanı (80 km transfer)" },
      { icon: "ri-bus-line", label: "Otobüs", detail: "Şanlıurfa'dan minibüs (1.5 saat)" },
      { icon: "ri-car-line", label: "Araç", detail: "Şanlıurfa'dan 80 km, 1 saat" },
    ],
    entry: [
      { label: "Tekne Turu (Fırat)", price: "₺200 – ₺400", note: "Kişi başı, tur süresine göre" },
      { label: "Rumkale", price: "₺100" },
    ],
    accommodation: [
      { name: "Halfeti Butik Otel", type: "Butik Otel", priceRange: "₺₺" },
      { name: "Şanlıurfa Otelleri", type: "Şehir Otelleri", priceRange: "₺₺" },
    ],
    tips: ["Nisan-Haziran siyah güller için en iyi dönem", "Tekne turu için sabah erken gidin", "Göbeklitepe ile birleştirin"],
    mapZoom: 14,
  },
  "sirince": {
    transport: [
      { icon: "ri-flight-takeoff-line", label: "Uçak", detail: "İzmir Adnan Menderes Havalimanı (80 km)" },
      { icon: "ri-bus-line", label: "Otobüs", detail: "Selçuk'tan dolmuş (10 dakika)" },
      { icon: "ri-car-line", label: "Araç", detail: "Selçuk'tan 8 km, 15 dakika" },
    ],
    entry: [
      { label: "Köy Girişi", price: "Ücretsiz" },
      { label: "Şarap Tadımı", price: "₺50 – ₺150", note: "Dükkan başına değişir" },
    ],
    accommodation: [
      { name: "Nişanyan Evleri", type: "Tarihi Taş Ev", priceRange: "₺₺₺₺" },
      { name: "Kirkinca Hotel", type: "Butik Otel", priceRange: "₺₺₺" },
      { name: "Selçuk Otelleri", type: "Şehir Otelleri", priceRange: "₺₺" },
    ],
    tips: ["Hafta içi gelin, hafta sonu çok kalabalık", "Sabah erken saatler en sakin dönem", "Efes ile aynı günde ziyaret edin"],
    mapZoom: 15,
  },
  "sumela-manastiri": {
    transport: [
      { icon: "ri-flight-takeoff-line", label: "Uçak", detail: "Trabzon Havalimanı (50 km, 1 saat transfer)" },
      { icon: "ri-bus-line", label: "Otobüs", detail: "Trabzon'dan servis araçları (1 saat)" },
      { icon: "ri-car-line", label: "Araç", detail: "Trabzon'dan 50 km, 1 saat" },
    ],
    entry: [
      { label: "Sümela Manastırı", price: "₺400" },
      { label: "Altındere Vadisi Milli Parkı", price: "₺50" },
    ],
    accommodation: [
      { name: "Trabzon Otelleri", type: "Şehir Otelleri", priceRange: "₺₺" },
      { name: "Maçka Pansiyonları", type: "Pansiyon", priceRange: "₺" },
    ],
    tips: ["Yürüyüş için rahat ayakkabı şart", "Sabah erken gidin, öğleden sonra kalabalık", "Trabzon şehriyle birleştirin"],
    mapZoom: 13,
  },
  "konya-mevlana": {
    transport: [
      { icon: "ri-flight-takeoff-line", label: "Uçak", detail: "Konya Havalimanı (18 km, 25 dakika transfer)" },
      { icon: "ri-bus-line", label: "Otobüs", detail: "Ankara'dan 3 saat, İstanbul'dan 7 saat" },
      { icon: "ri-train-line", label: "Hızlı Tren", detail: "Ankara'dan YHT ile 1.5 saat" },
    ],
    entry: [
      { label: "Mevlana Müzesi", price: "₺200" },
      { label: "Karatay Medresesi", price: "₺100" },
      { label: "İnce Minareli Medrese", price: "₺100" },
    ],
    accommodation: [
      { name: "Hilton Garden Inn Konya", type: "Zincir Otel", priceRange: "₺₺₺" },
      { name: "Rumi Hotel", type: "Butik Otel", priceRange: "₺₺₺" },
      { name: "Konya Otelleri", type: "Şehir Otelleri", priceRange: "₺₺" },
    ],
    tips: ["Aralık'ta Şeb-i Arus için çok önceden rezervasyon yapın", "Müze girişinde başörtüsü gerekebilir", "Etli ekmek için Konya Etli Ekmek Fırını'na gidin"],
    mapZoom: 14,
  },
  "alacati": {
    transport: [
      { icon: "ri-flight-takeoff-line", label: "Uçak", detail: "İzmir Adnan Menderes Havalimanı (80 km, 1 saat transfer)" },
      { icon: "ri-bus-line", label: "Otobüs", detail: "İzmir'den Çeşme'ye otobüs, oradan dolmuş" },
      { icon: "ri-car-line", label: "Araç", detail: "İzmir'den 80 km, 1 saat" },
    ],
    entry: [
      { label: "Köy Girişi", price: "Ücretsiz" },
      { label: "Rüzgar Sörfü Dersi", price: "₺1.500 – ₺2.500", note: "Kişi başı, okula göre değişir" },
    ],
    accommodation: [
      { name: "Taş Otel Alaçatı", type: "Butik Taş Otel", priceRange: "₺₺₺₺" },
      { name: "Alaçatı Otantik Otel", type: "Butik Otel", priceRange: "₺₺₺" },
      { name: "Çeşme Otelleri", type: "Sahil Otelleri", priceRange: "₺₺₺" },
    ],
    tips: ["Temmuz-Ağustos çok kalabalık, Haziran veya Eylül tercih edin", "Hafta sonu brunch için rezervasyon şart", "Rüzgar sörfü için Ekim en iyi dönem"],
    mapZoom: 15,
  },
  "edirne-selimiye": {
    transport: [
      { icon: "ri-bus-line", label: "Otobüs", detail: "İstanbul'dan 2.5 saat (E80 otoyolu)" },
      { icon: "ri-car-line", label: "Araç", detail: "İstanbul'dan 230 km, 2.5 saat" },
      { icon: "ri-train-line", label: "Tren", detail: "İstanbul Halkalı'dan tren bağlantısı mevcut" },
    ],
    entry: [
      { label: "Selimiye Camii", price: "Ücretsiz (bağış)" },
      { label: "Selimiye Vakıf Müzesi", price: "₺100" },
      { label: "Edirne Arkeoloji Müzesi", price: "₺100" },
    ],
    accommodation: [
      { name: "Tuna Hotel Edirne", type: "Şehir Oteli", priceRange: "₺₺" },
      { name: "Edirne Palace Hotel", type: "Şehir Oteli", priceRange: "₺₺" },
    ],
    tips: ["İstanbul'dan günübirlik yapılabilir", "Kırkpınar için Haziran-Temmuz'da gelin", "Ciğer tava ve badem ezmesi mutlaka deneyin"],
    mapZoom: 14,
  },
  "trabzon-uzungol": {
    transport: [
      { icon: "ri-flight-takeoff-line", label: "Uçak", detail: "Trabzon Havalimanı (100 km, 1.5 saat transfer)" },
      { icon: "ri-bus-line", label: "Otobüs", detail: "Trabzon'dan minibüs (1.5 saat)" },
      { icon: "ri-car-line", label: "Araç", detail: "Trabzon'dan 100 km, 1.5 saat (dağ yolu)" },
    ],
    entry: [
      { label: "Uzungöl Tabiat Parkı", price: "₺50" },
    ],
    accommodation: [
      { name: "Inan Kardesler Hotel", type: "Göl Kenarı Otel", priceRange: "₺₺₺" },
      { name: "Uzungöl Pansiyonları", type: "Pansiyon", priceRange: "₺₺" },
    ],
    tips: ["Sabah erken saatlerde sis manzarası için 06:00'da kalkın", "Kış aylarında kar manzarası büyülüür", "Trabzon şehriyle birleştirin"],
    mapZoom: 14,
  },
  "van-akdamar": {
    transport: [
      { icon: "ri-flight-takeoff-line", label: "Uçak", detail: "Van Ferit Melen Havalimanı (6 km, 15 dakika transfer)" },
      { icon: "ri-bus-line", label: "Otobüs", detail: "Ankara'dan 14 saat, İstanbul'dan 18 saat" },
    ],
    entry: [
      { label: "Akdamar Adası & Kilise", price: "₺200 + tekne ücreti" },
      { label: "Tekne (Akdamar)", price: "₺150 – ₺200", note: "Kişi başı" },
      { label: "Van Kalesi", price: "₺100" },
    ],
    accommodation: [
      { name: "Büyük Urartu Hotel", type: "Şehir Oteli", priceRange: "₺₺₺" },
      { name: "Merit Asur Hotel", type: "Şehir Oteli", priceRange: "₺₺" },
    ],
    tips: ["Van kahvaltısı için sabah erken kalkın", "Van Kedisi Evi'ni ziyaret edin", "Nemrut Dağı ile birleştirin"],
    mapZoom: 12,
  },
  "ishak-pasa-sarayi": {
    transport: [
      { icon: "ri-flight-takeoff-line", label: "Uçak", detail: "Ağrı Havalimanı (95 km transfer) veya Van Havalimanı" },
      { icon: "ri-bus-line", label: "Otobüs", detail: "Doğubayazıt'tan taksi veya dolmuş (6 km)" },
    ],
    entry: [
      { label: "İshak Paşa Sarayı", price: "₺200" },
    ],
    accommodation: [
      { name: "Doğubayazıt Otelleri", type: "Şehir Otelleri", priceRange: "₺₺" },
      { name: "Isfahan Hotel", type: "Şehir Oteli", priceRange: "₺₺" },
    ],
    tips: ["Gün batımında saray altın rengine bürünür, 17:00'da gidin", "Ağrı Dağı manzarası için sabah erken gidin", "Van ile birleştirin"],
    mapZoom: 14,
  },
  "diyarbakir-surlar": {
    transport: [
      { icon: "ri-flight-takeoff-line", label: "Uçak", detail: "Diyarbakır Havalimanı (5 km, 15 dakika transfer)" },
      { icon: "ri-bus-line", label: "Otobüs", detail: "Ankara'dan 10 saat, İstanbul'dan 14 saat" },
    ],
    entry: [
      { label: "Tarihi Surlar (dış)", price: "Ücretsiz" },
      { label: "Diyarbakır Müzesi", price: "₺100" },
      { label: "Ulu Cami", price: "Ücretsiz" },
    ],
    accommodation: [
      { name: "Büyük Kervansaray Hotel", type: "Tarihi Kervansaray", priceRange: "₺₺₺" },
      { name: "Class Hotel Diyarbakır", type: "Şehir Oteli", priceRange: "₺₺" },
    ],
    tips: ["Surlar üzerinde yürüyüş yapın", "Ciğer kebabı için tarihi çarşıya gidin", "Mardin ile birleştirin"],
    mapZoom: 14,
  },
  "antalya-kaleiçi": {
    transport: [
      { icon: "ri-flight-takeoff-line", label: "Uçak", detail: "Antalya Havalimanı (12 km, 20 dakika transfer)" },
      { icon: "ri-bus-line", label: "Otobüs", detail: "İstanbul'dan 10 saat, Ankara'dan 6 saat" },
    ],
    entry: [
      { label: "Hadrian Kapısı", price: "Ücretsiz" },
      { label: "Antalya Müzesi", price: "₺200" },
      { label: "Perge Antik Kenti", price: "₺400", note: "Şehir dışında, 18 km" },
    ],
    accommodation: [
      { name: "Tuvana Hotel", type: "Butik Konak Otel", priceRange: "₺₺₺₺" },
      { name: "Alp Paşa Hotel", type: "Tarihi Konak", priceRange: "₺₺₺" },
      { name: "Kaleiçi Butik Oteller", type: "Butik Otel", priceRange: "₺₺₺" },
    ],
    tips: ["Kaleiçi'nde konaklayın, atmosferi tamamlar", "Akşam limanda yürüyüş yapın", "Perge, Aspendos ve Termessos ile birleştirin"],
    mapZoom: 15,
  },
  "kirklareli-igneada": {
    transport: [
      { icon: "ri-bus-line", label: "Otobüs", detail: "İstanbul'dan 3 saat (Kırklareli üzerinden)" },
      { icon: "ri-car-line", label: "Araç", detail: "İstanbul'dan 200 km, 2.5 saat" },
    ],
    entry: [
      { label: "İğneada Longoz Ormanları Milli Parkı", price: "₺50" },
      { label: "İğneada Plajı", price: "Ücretsiz" },
    ],
    accommodation: [
      { name: "İğneada Pansiyonları", type: "Pansiyon", priceRange: "₺₺" },
      { name: "Kırklareli Otelleri", type: "Şehir Otelleri", priceRange: "₺₺" },
    ],
    tips: ["İlkbaharda longoz ormanları en güzel halindedir", "Kuş gözlemi için sabah erken gidin", "İstanbul'dan hafta sonu kaçamağı için ideal"],
    mapZoom: 13,
  },
  "trakya-vize": {
    transport: [
      { icon: "ri-bus-line", label: "Otobüs", detail: "İstanbul'dan 2.5 saat" },
      { icon: "ri-car-line", label: "Araç", detail: "İstanbul'dan 180 km, 2 saat" },
    ],
    entry: [
      { label: "Vize Kalesi & Surlar", price: "Ücretsiz" },
      { label: "Bağ Turları", price: "₺200 – ₺500", note: "Şarap tadımı dahil" },
    ],
    accommodation: [
      { name: "Vize Pansiyonları", type: "Pansiyon", priceRange: "₺" },
      { name: "Kırklareli Otelleri", type: "Şehir Otelleri", priceRange: "₺₺" },
    ],
    tips: ["Hasat döneminde (Eylül-Ekim) bağ turları düzenlenir", "İstanbul'dan günübirlik yapılabilir", "Edirne ile birleştirin"],
    mapZoom: 14,
  },
  "bursa-uludag": {
    transport: [
      { icon: "ri-flight-takeoff-line", label: "Uçak", detail: "Bursa Yenişehir Havalimanı (50 km) veya İstanbul'dan feribot+otobüs" },
      { icon: "ri-ship-line", label: "Feribot", detail: "İstanbul Yenikapı'dan Bursa'ya hızlı feribot (1.5 saat)" },
      { icon: "ri-bus-line", label: "Otobüs", detail: "İstanbul'dan 3 saat, Ankara'dan 4 saat" },
    ],
    entry: [
      { label: "Yeşil Cami & Türbe", price: "Ücretsiz" },
      { label: "Uludağ Teleferik", price: "₺400 – ₺600", note: "Gidiş-dönüş" },
      { label: "Uludağ Kayak Merkezi", price: "₺800 – ₺1.500", note: "Günlük kayak kartı" },
    ],
    accommodation: [
      { name: "Kervansaray Bursa Termal", type: "Termal Otel", priceRange: "₺₺₺" },
      { name: "Uludağ Ski Lodge", type: "Kayak Oteli", priceRange: "₺₺₺" },
      { name: "Bursa Şehir Otelleri", type: "Şehir Otelleri", priceRange: "₺₺" },
    ],
    tips: ["İstanbul'dan feribot ile gitmek çok keyifli", "Kış için Uludağ, yaz için şehir turu öneririz", "İskender kebabı için Kebapçı İskender'e gidin"],
    mapZoom: 13,
  },
  "iznik": {
    transport: [
      { icon: "ri-bus-line", label: "Otobüs", detail: "Bursa'dan 1 saat, İstanbul'dan 2.5 saat" },
      { icon: "ri-car-line", label: "Araç", detail: "İstanbul'dan 150 km, 2 saat" },
      { icon: "ri-ship-line", label: "Feribot", detail: "İstanbul Yenikapı'dan Bursa'ya feribot, oradan İznik" },
    ],
    entry: [
      { label: "İznik Müzesi (Ayasofya)", price: "₺150" },
      { label: "Antik Surlar (dış)", price: "Ücretsiz" },
      { label: "Çini Atölyeleri", price: "Ücretsiz (tadım)" },
    ],
    accommodation: [
      { name: "Çamlık Motel İznik", type: "Göl Kenarı Otel", priceRange: "₺₺" },
      { name: "İznik Gölü Pansiyonları", type: "Pansiyon", priceRange: "₺₺" },
    ],
    tips: ["Çini atölyelerinde el yapımı çini satın alın", "İznik Gölü kıyısında balık yiyin", "Bursa ile aynı günde ziyaret edin"],
    mapZoom: 14,
  },
  "ani-harabeleri": {
    transport: [
      { icon: "ri-flight-takeoff-line", label: "Uçak", detail: "Kars Havalimanı (45 km, 45 dakika transfer)" },
      { icon: "ri-bus-line", label: "Otobüs", detail: "Erzurum'dan 3 saat, Ankara'dan 14 saat" },
    ],
    entry: [
      { label: "Ani Arkeoloji Parkı", price: "₺200" },
      { label: "Şanlıurfa Arkeoloji Müzesi", price: "₺150", note: "Mutlaka görün" },
    ],
    accommodation: [
      { name: "Kars Otelleri", type: "Şehir Otelleri", priceRange: "₺₺" },
      { name: "Katerina Sarayı Hotel", type: "Tarihi Otel", priceRange: "₺₺₺" },
    ],
    tips: ["Kars şehri ile birleştirin, gravyer peyniri alın", "Rüzgarlı olabilir, rüzgarlık giyin", "Rehberli tur almak tarihi zenginleştirir"],
    mapZoom: 13,
  },
};

// ─────────────────────────────────────────────────────────────

const PlaceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const place = places.find((p) => p.id === id);
  const [activeImg, setActiveImg] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [practicalTab, setPracticalTab] = useState<"transport" | "entry" | "accommodation" | "tips">("transport");

  const practical = id ? (practicalData[id] || null) : null;
  const [showVisitPlan, setShowVisitPlan] = useState(false);
  const viewCount = usePageView(id || "", "place");

  // ── SEO ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!place || !id) return;

    const pageTitle = `${place.name} — ${place.city} Gezilecek Yerler Rehberi | Türkiye Seyahat`;
    const pageDesc = `${place.shortDesc} ${place.city}, ${place.region} bölgesinde yer alan ${place.name} hakkında kapsamlı gezi rehberi, fotoğraflar ve pratik bilgiler.`.slice(0, 160);
    const pageKeywords = `${place.name}, ${place.city} gezilecek yerler, ${place.region} gezi rehberi, ${place.tags.join(", ")}`;
    const canonicalUrl = `https://geziturkiyem.com/place/${id}`;
    const allImgs = place.images && place.images.length > 0 ? place.images : [place.image];

    document.title = pageTitle;
    setMetaTag("description", pageDesc);
    setMetaTag("keywords", pageKeywords);
    setMetaTag("robots", "index, follow");
    setLinkTag("canonical", canonicalUrl);

    // Open Graph
    setMetaTag("og:title", pageTitle, true);
    setMetaTag("og:description", pageDesc, true);
    setMetaTag("og:image", place.image, true);
    setMetaTag("og:image:width", "800", true);
    setMetaTag("og:image:height", "450", true);
    setMetaTag("og:image:alt", place.name, true);
    setMetaTag("og:image:type", "image/jpeg", true);
    setMetaTag("og:url", canonicalUrl, true);
    setMetaTag("og:type", "article", true);
    setMetaTag("og:locale", "tr_TR", true);
    setMetaTag("og:site_name", "Yerel Bilgi — Türkiye Seyahat Rehberi", true);

    // Twitter Card
    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:site", "@geziturkiyem");
    setMetaTag("twitter:title", pageTitle);
    setMetaTag("twitter:description", pageDesc);
    setMetaTag("twitter:image", place.image);
    setMetaTag("twitter:image:alt", place.name);

    setMetaTag("geo.placename", place.city);
    setMetaTag("geo.region", "TR");

    // TouristAttraction schema
    injectSchema(id, {
      "@context": "https://schema.org",
      "@type": "TouristAttraction",
      "name": place.name,
      "description": pageDesc,
      "image": allImgs.map((img) => ({
        "@type": "ImageObject",
        "url": img,
        "width": 800,
        "height": 450,
      })),
      "url": canonicalUrl,
      "touristType": place.tags,
      "inLanguage": "tr-TR",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": place.city,
        "addressRegion": place.region,
        "addressCountry": "TR",
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": place.rating,
        "bestRating": 5,
        "worstRating": 1,
        "ratingCount": 120,
      },
      "isAccessibleForFree": true,
      "publicAccess": true,
      "publisher": {
        "@type": "Organization",
        "name": "Yerel Bilgi — Türkiye Seyahat Rehberi",
        "url": "https://geziturkiyem.com",
      },
    });

    // BreadcrumbList schema
    const breadcrumbId = `schema-bc-place-${id}`;
    const existingBc = document.getElementById(breadcrumbId);
    if (existingBc) existingBc.remove();
    const bcScript = document.createElement("script");
    bcScript.id = breadcrumbId;
    bcScript.type = "application/ld+json";
    bcScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Ana Sayfa", "item": "https://geziturkiyem.com/" },
        { "@type": "ListItem", "position": 2, "name": "Mekanlar", "item": "https://geziturkiyem.com/places" },
        { "@type": "ListItem", "position": 3, "name": place.name, "item": canonicalUrl },
      ],
    });
    document.head.appendChild(bcScript);

    return () => {
      document.title = "Türkiye Seyahat Rehberi";
      document.getElementById(`schema-place-${id}`)?.remove();
      document.getElementById(`schema-bc-place-${id}`)?.remove();
    };
  }, [id, place]);
  // ─────────────────────────────────────────────────────────────

  if (!place) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-stone-400 mb-4">Yer bulunamadı.</p>
          <button onClick={() => navigate("/places")} className="text-amber-600 font-medium cursor-pointer">
            Geri Dön
          </button>
        </div>
      </div>
    );
  }

  const allImages = place.images && place.images.length > 0 ? place.images : [place.image];

  const handlePrev = () => setActiveImg((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  const handleNext = () => setActiveImg((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));

  const practicalTabs = [
    { key: "transport" as const, label: "Ulaşım", icon: "ri-road-map-line" },
    { key: "entry" as const, label: "Giriş Ücretleri", icon: "ri-ticket-line" },
    { key: "accommodation" as const, label: "Konaklama", icon: "ri-hotel-line" },
    { key: "tips" as const, label: "İpuçları", icon: "ri-lightbulb-line" },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Image with Gallery */}
      <div className="relative h-[65vh] overflow-hidden bg-stone-900">
        <img
          src={allImages[activeImg]}
          alt={`${place.name} - ${activeImg + 1}`}
          className="w-full h-full object-cover object-top transition-opacity duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/20"></div>

        <button
          onClick={() => navigate("/places")}
          className="absolute top-24 left-6 flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 text-white text-sm px-4 py-2 rounded-full cursor-pointer hover:bg-white/30 transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i> Geri
        </button>

        <button
          onClick={() => setLightboxOpen(true)}
          className="absolute top-24 right-6 flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 text-white text-sm px-4 py-2 rounded-full cursor-pointer hover:bg-white/30 transition-colors whitespace-nowrap"
        >
          <i className="ri-fullscreen-line"></i> Büyüt
        </button>

        {allImages.length > 1 && (
          <>
            <button
              onClick={handlePrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/40 hover:bg-black/60 text-white rounded-full cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button
              onClick={handleNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/40 hover:bg-black/60 text-white rounded-full cursor-pointer transition-colors"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </>
        )}

        <div className="absolute top-24 left-1/2 -translate-x-1/2 bg-black/40 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full">
          {activeImg + 1} / {allImages.length}
        </div>

        <div className="absolute bottom-8 left-0 right-0 px-6 md:px-16">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-wrap gap-2 mb-3">
              {place.tags.map((tag) => (
                <span key={tag} className="bg-white/20 backdrop-blur-sm border border-white/30 text-white text-xs px-3 py-1 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              {place.name}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
              <div className="flex items-center gap-1">
                <i className="ri-map-pin-line text-amber-400"></i>
                <span>{place.city}, {place.region}</span>
              </div>
              <div className="flex items-center gap-1">
                <i className="ri-star-fill text-amber-400"></i>
                <span>{place.rating} / 5</span>
              </div>
              <div className="flex items-center gap-1">
                <i className="ri-calendar-line text-amber-400"></i>
                <span>{place.bestTime}</span>
              </div>
              {viewCount > 0 && (
                <div className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm border border-white/20 px-3 py-1 rounded-full">
                  <i className="ri-eye-line text-amber-300 text-xs"></i>
                  <span className="text-white text-xs font-semibold">
                    {viewCount.toLocaleString("tr-TR")} görüntülenme
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Thumbnail Strip */}
      {allImages.length > 1 && (
        <div className="bg-stone-900 py-3 px-4">
          <div className="max-w-5xl mx-auto flex gap-2 overflow-x-auto pb-1">
            {allImages.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setActiveImg(idx)}
                className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                  activeImg === idx ? "border-amber-400 opacity-100" : "border-transparent opacity-50 hover:opacity-80"
                }`}
              >
                <img src={img} alt={`${place.name} ${idx + 1}`} className="w-full h-full object-cover object-top" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-12">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: "Ana Sayfa", href: "/" },
            { label: "Mekanlar", href: "/places" },
            { label: place.city, href: `/places` },
            { label: place.name },
          ]}
          className="mb-8 text-stone-400"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <div className="md:col-span-2">
            <h2 className="text-2xl font-bold text-stone-900 mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
              Hakkında
            </h2>
            <div className="text-stone-600 text-sm leading-relaxed space-y-4">
              {place.description.split("\n\n").map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>

            {/* Photo Gallery Grid */}
            {allImages.length > 1 && (
              <div className="mt-10">
                <h2 className="text-xl font-bold text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Fotoğraf Galerisi
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {allImages.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => { setActiveImg(idx); setLightboxOpen(true); }}
                      className="relative group overflow-hidden rounded-xl cursor-pointer"
                    >
                      <div className="w-full h-36">
                        <img
                          src={img}
                          alt={`${place.name} - Açı ${idx + 1}`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                        <div className="w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <i className="ri-zoom-in-line text-white text-lg"></i>
                        </div>
                      </div>
                      <div className="absolute bottom-2 left-2 bg-black/50 text-white text-xs px-2 py-0.5 rounded-full">
                        {idx + 1}. Açı
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Practical Info Section ── */}
            {practical && (
              <div className="mt-12">
                <h2 className="text-xl font-bold text-stone-900 mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Pratik Bilgiler
                </h2>
                {/* Tabs */}
                <div className="flex gap-2 flex-wrap mb-5">
                  {practicalTabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setPracticalTab(tab.key)}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        practicalTab === tab.key
                          ? "bg-stone-900 text-white"
                          : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                      }`}
                    >
                      <i className={`${tab.icon} text-xs`}></i>
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Tab Content */}
                {practicalTab === "transport" && (
                  <div className="space-y-3">
                    {practical.transport.map((t) => (
                      <div key={t.label} className="flex items-start gap-4 p-4 bg-stone-50 rounded-xl">
                        <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                          <i className={`${t.icon} text-amber-600`}></i>
                        </div>
                        <div>
                          <p className="font-semibold text-stone-900 text-sm">{t.label}</p>
                          <p className="text-stone-500 text-xs mt-0.5 leading-relaxed">{t.detail}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {practicalTab === "entry" && (
                  <div className="space-y-3">
                    {practical.entry.map((e) => (
                      <div key={e.label} className="flex items-center justify-between gap-4 p-4 bg-stone-50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                            <i className="ri-ticket-line text-amber-600 text-sm"></i>
                          </div>
                          <div>
                            <p className="font-semibold text-stone-900 text-sm">{e.label}</p>
                            {e.note && <p className="text-stone-400 text-xs mt-0.5">{e.note}</p>}
                          </div>
                        </div>
                        <span className={`font-bold text-sm whitespace-nowrap flex-shrink-0 ${e.price === "Ücretsiz" ? "text-emerald-600" : "text-amber-600"}`}>
                          {e.price}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {practicalTab === "accommodation" && (
                  <div className="space-y-3">
                    {practical.accommodation.map((a) => (
                      <div key={a.name} className="flex items-center justify-between gap-4 p-4 bg-stone-50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                            <i className="ri-hotel-line text-amber-600 text-sm"></i>
                          </div>
                          <div>
                            <p className="font-semibold text-stone-900 text-sm">{a.name}</p>
                            <p className="text-stone-400 text-xs mt-0.5">{a.type}</p>
                          </div>
                        </div>
                        <span className="font-bold text-amber-600 text-sm whitespace-nowrap flex-shrink-0">{a.priceRange}</span>
                      </div>
                    ))}
                  </div>
                )}

                {practicalTab === "tips" && (
                  <div className="space-y-3">
                    {practical.tips.map((tip, i) => (
                      <div key={i} className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-100">
                        <div className="w-6 h-6 flex items-center justify-center bg-amber-200 rounded-full flex-shrink-0 mt-0.5">
                          <span className="text-amber-700 font-bold text-xs">{i + 1}</span>
                        </div>
                        <p className="text-stone-700 text-sm leading-relaxed">{tip}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* City FAQ Internal Link Box */}
            {(() => {
              const faqLink = place.city ? placeCityFaqLinks[place.city] : null;
              if (!faqLink) return null;
              return (
                <div className="mt-10 p-5 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0">
                    <i className="ri-question-answer-line text-amber-600 text-lg"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1">Merak Ettikleriniz</p>
                    <p className="text-stone-700 text-sm mb-3 leading-relaxed">
                      {place.city} hakkında sık sorulan soruları ve pratik seyahat bilgilerini derledik.
                    </p>
                    <Link
                      to={faqLink.path}
                      className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-arrow-right-circle-line"></i>
                      {faqLink.label}
                    </Link>
                  </div>
                </div>
              );
            })()}

            {/* ── Map Section ── */}
            <div className="mt-12">
              <h2 className="text-xl font-bold text-stone-900 mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
                Konum
              </h2>
              <div className="rounded-2xl overflow-hidden border border-stone-100" style={{ height: "340px" }}>
                <iframe
                  title={`${place.name} harita`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(place.name + " " + place.city + " Turkey")}&z=${practical?.mapZoom ?? 13}&output=embed`}
                ></iframe>
              </div>
              <div className="flex items-center gap-2 mt-3 text-sm text-stone-500">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-map-pin-line text-amber-500"></i>
                </div>
                <span>{place.city}, {place.region}</span>
                <a
                  href={`https://www.google.com/maps/search/${encodeURIComponent(place.name + " " + place.city + " Turkey")}`}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="ml-auto text-amber-600 hover:text-amber-700 text-xs font-medium cursor-pointer whitespace-nowrap"
                >
                  Google Maps'te Aç <i className="ri-external-link-line"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <div className="bg-amber-50 rounded-2xl p-5">
              <h3 className="font-bold text-stone-900 text-sm mb-3">Öne Çıkan Noktalar</h3>
              <ul className="space-y-2">
                {place.highlights.map((h) => (
                  <li key={h} className="flex items-center gap-2 text-sm text-stone-600">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-checkbox-circle-fill text-amber-500 text-xs"></i>
                    </div>
                    {h}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-stone-50 rounded-2xl p-5">
              <h3 className="font-bold text-stone-900 text-sm mb-3">Pratik Bilgi</h3>
              <div className="space-y-2 text-sm text-stone-600">
                <div className="flex items-center gap-2">
                  <i className="ri-calendar-line text-amber-500"></i>
                  <span>En iyi zaman: {place.bestTime}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-amber-500"></i>
                  <span>{place.city}, {place.region}</span>
                </div>
                {practical && (
                  <div className="flex items-center gap-2">
                    <i className="ri-ticket-line text-amber-500"></i>
                    <span>{practical.entry[0]?.price === "Ücretsiz" ? "Ücretsiz giriş" : `Giriş: ${practical.entry[0]?.price}`}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-stone-900 rounded-2xl p-5 text-center">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                <i className="ri-camera-line text-amber-400 text-2xl"></i>
              </div>
              <p className="text-white font-bold text-lg">{allImages.length} Farklı Açı</p>
              <p className="text-stone-400 text-xs mt-1">Fotoğraf galerisini keşfet</p>
            </div>

            {/* View Counter Card */}
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-xl">
                  <i className="ri-eye-line text-amber-600 text-lg"></i>
                </div>
                <div>
                  <p className="font-bold text-stone-900 text-sm">Ziyaretçi Sayacı</p>
                  <p className="text-stone-400 text-xs">Bu sayfaya bakıldı</p>
                </div>
              </div>
              <p className="text-3xl font-bold text-amber-600" style={{ fontFamily: "'Playfair Display', serif" }}>
                {viewCount > 0 ? viewCount.toLocaleString("tr-TR") : "—"}
              </p>
              <p className="text-xs text-stone-400 mt-1">toplam görüntülenme</p>
              <div className="mt-3 pt-3 border-t border-amber-100">
                <div className="flex items-center gap-1.5 text-xs text-stone-500">
                  <i className="ri-bar-chart-line text-amber-500"></i>
                  <span>Gerçek zamanlı takip</span>
                </div>
              </div>
            </div>

            {/* Visit Plan CTA */}
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg">
                  <i className="ri-calendar-check-line text-amber-600"></i>
                </div>
                <div>
                  <p className="font-bold text-stone-900 text-sm">Ziyaret Planı</p>
                  <p className="text-stone-400 text-xs">Günlük program önerisi</p>
                </div>
              </div>
              <p className="text-stone-500 text-xs mb-4 leading-relaxed">
                Kaç gün kalacağınızı seçin, size özel günlük program hazırlayalım.
              </p>
              <button
                onClick={() => setShowVisitPlan(true)}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-map-2-line"></i>
                Plan Oluştur
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Similar Places in Same City ── */}
      {(() => {
        const sameCityPlaces = places.filter((p) => p.city === place.city && p.id !== id).slice(0, 4);
        const sameCityRestaurants = restaurants.filter((r) => r.city === place.city).slice(0, 3);
        if (sameCityPlaces.length === 0 && sameCityRestaurants.length === 0) return null;
        return (
          <section className="bg-stone-50 py-14">
            <div className="max-w-5xl mx-auto px-4 md:px-8">
              {/* Same city places */}
              {sameCityPlaces.length > 0 && (
                <div className="mb-12">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-1 h-6 bg-amber-400 rounded-full"></div>
                    <h2 className="text-xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {place.city}'de Gezilecek Diğer Yerler
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {sameCityPlaces.map((p) => (
                      <Link
                        key={p.id}
                        to={`/place/${p.id}`}
                        className="group bg-white rounded-2xl overflow-hidden border border-stone-100 hover:border-amber-200 hover:scale-[1.01] transition-all duration-200 block"
                      >
                        <div className="relative h-36 overflow-hidden">
                          <img
                            src={p.image}
                            alt={p.name}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                          <div className="absolute bottom-2 left-2 right-2">
                            <p className="text-white font-bold text-sm leading-tight line-clamp-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                              {p.name}
                            </p>
                          </div>
                          <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/40 backdrop-blur-sm rounded-full px-2 py-0.5">
                            <i className="ri-star-fill text-amber-400 text-xs"></i>
                            <span className="text-white text-xs font-semibold">{p.rating}</span>
                          </div>
                        </div>
                        <div className="p-3">
                          <div className="flex flex-wrap gap-1">
                            {p.tags.slice(0, 2).map((tag) => (
                              <span key={tag} className="bg-stone-100 text-stone-500 text-xs px-2 py-0.5 rounded-full">{tag}</span>
                            ))}
                          </div>
                          <div className="flex items-center gap-1 mt-2 text-amber-600 text-xs font-medium">
                            <span>Keşfet</span>
                            <i className="ri-arrow-right-line"></i>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Same city restaurants */}
              {sameCityRestaurants.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-1 h-6 bg-rose-400 rounded-full"></div>
                    <h2 className="text-xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {place.city}'de Lezzet Durakları
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {sameCityRestaurants.map((r) => (
                      <Link
                        key={r.id}
                        to={`/restaurant/${r.id}`}
                        className="group bg-white rounded-2xl overflow-hidden border border-stone-100 hover:border-rose-200 hover:scale-[1.01] transition-all duration-200 flex gap-3 p-3"
                      >
                        <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
                          <img src={r.image} alt={r.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-stone-900 text-sm leading-snug line-clamp-2 group-hover:text-rose-700 transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
                            {r.name}
                          </h3>
                          <p className="text-stone-400 text-xs mt-0.5">{r.cuisine}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex items-center gap-0.5">
                              <i className="ri-star-fill text-amber-400 text-xs"></i>
                              <span className="text-xs font-semibold text-stone-700">{r.rating}</span>
                            </div>
                            <span className="text-xs font-bold text-amber-600">{r.priceRange}</span>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {r.mustTry.slice(0, 1).map((item) => (
                              <span key={item} className="bg-rose-50 text-rose-600 text-xs px-2 py-0.5 rounded-full">{item}</span>
                            ))}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                  <div className="mt-4 text-center">
                    <Link
                      to={`/restoranlar?city=${place.city.toLowerCase()}`}
                      className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-amber-600 transition-colors cursor-pointer"
                    >
                      {place.city}'deki tüm restoranları gör
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </section>
        );
      })()}

      {/* Visit Plan Modal */}
      {showVisitPlan && place && (
        <VisitPlanModal
          placeId={id || ""}
          placeName={place.name}
          onClose={() => setShowVisitPlan(false)}
        />
      )}

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center text-white bg-white/10 hover:bg-white/20 rounded-full cursor-pointer transition-colors"
            onClick={() => setLightboxOpen(false)}
          >
            <i className="ri-close-line text-xl"></i>
          </button>

          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full cursor-pointer transition-colors"
            onClick={(e) => { e.stopPropagation(); handlePrev(); }}
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>

          <div className="max-w-5xl w-full px-16" onClick={(e) => e.stopPropagation()}>
            <div className="w-full h-[75vh]">
              <img
                src={allImages[activeImg]}
                alt={`${place.name} - ${activeImg + 1}`}
                className="w-full h-full object-contain"
              />
            </div>
            <div className="text-center mt-3 text-white/60 text-sm">
              {place.name} — {activeImg + 1}. Açı ({activeImg + 1}/{allImages.length})
            </div>
            <div className="flex justify-center gap-2 mt-3 flex-wrap">
              {allImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImg(idx)}
                  className={`w-14 h-10 rounded overflow-hidden border-2 cursor-pointer transition-all ${
                    activeImg === idx ? "border-amber-400" : "border-transparent opacity-50 hover:opacity-80"
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover object-top" />
                </button>
              ))}
            </div>
          </div>

          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full cursor-pointer transition-colors"
            onClick={(e) => { e.stopPropagation(); handleNext(); }}
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default PlaceDetail;
